import { StandaloneSearchBox } from '@react-google-maps/api';
import { useRef } from 'react';

import { useGoogleMaps } from '../../contexts/google-map-context/GoogleMapContent';

interface CustomInputProperties {
  inputLabel?: string;
  placeholder: string;
  value: string | number | null;
  name: string;
  onChangeValue: (
    name: string,
    address: string | number,
    latitude: number | null,
    longitude: number | null,
  ) => void;
  valueRequired?: boolean;
  readOnly?: boolean;
  error?: boolean;
  errorMessage?: string;
}

/**
 * Custom address input box with google places api integrated
 */
function CustomAddressInput({
  inputLabel,
  name,
  placeholder,
  value,
  onChangeValue,
  valueRequired,
  readOnly,
  error,
  errorMessage,
}: CustomInputProperties) {
  const inputReference = useRef<google.maps.places.SearchBox | null>(null);

  const { isLoaded } = useGoogleMaps();

  /**
   * Handle input change
   */
  const handleValueChange = (changeValue: string | number) => {
    onChangeValue(name, changeValue, null, null);
  };

  /**
   * Handles reference load for the StandaloneSearchBox.
   */
  const handleReferenceLoad = (reference: google.maps.places.SearchBox | null) => {
    inputReference.current = reference;
  };

  /**
   * Just a place change
   */
  const handlePlaceChange = () => {
    if (inputReference.current) {
      const places = inputReference.current.getPlaces();

      if (places?.length) {
        const address = places[0];

        // Ensure geometry and viewport exist
        if (!address?.geometry || !address?.geometry?.viewport) {
          onChangeValue(name, address?.name || '', null, null);
          return;
        }

        const viewport = address?.geometry?.viewport;
        const northeast = viewport?.getNorthEast();
        const southwest = viewport?.getSouthWest();

        if (!northeast || !southwest) {
          onChangeValue(name, address?.name || '', null, null);
          return;
        }

        const avgLat = (northeast.lat() + southwest.lat()) / 2;
        const avgLong = (northeast.lng() + southwest.lng()) / 2;

        // Create a unique address string
        const addressComponents = [
          address.name,
          ...(address.address_components?.map((comp) => comp?.long_name) || []),
        ];
        const uniqueStrings = [...new Set(addressComponents)].join(', ');

        onChangeValue(name, uniqueStrings, avgLat, avgLong);
      }
    }
  };

  return (
    <div className="custom-address-input-wrap">
      {inputLabel && (
        <p className={`input-label ${error && 'error'}`}>
          {inputLabel}
          {valueRequired && <span>*</span>}
        </p>
      )}
      <div className={`custom-input-container ${error && 'error'}`}>
        {isLoaded && (
          <StandaloneSearchBox
            onLoad={(searchBox) => handleReferenceLoad(searchBox as google.maps.places.SearchBox)}
            onPlacesChanged={handlePlaceChange}>
            <input
              type="text"
              placeholder={placeholder}
              value={value !== null ? value : undefined}
              onChange={(event) => handleValueChange(event.target.value)}
              required={valueRequired}
              readOnly={readOnly}
            />
          </StandaloneSearchBox>
        )}
      </div>
      {error && <p className="custom-input-error-message">{errorMessage}</p>}
    </div>
  );
}

CustomAddressInput.defaultProps = {
  inputLabel: '',
  valueRequired: false,
  readOnly: false,
  error: false,
  errorMessage: '',
};

export default CustomAddressInput;
